import { useTranslation } from 'react-i18next';
import { FiCalendar } from 'react-icons/fi';
import { MdCheckCircle, MdSchedule, MdOutlineInfo } from 'react-icons/md';
import { BiWallet } from 'react-icons/bi';
import { Job } from '../../../utils/type';
import { formatCurrency } from '../../../utils/currency';

interface PaymentScheduleBreakdownProps {
  schedule: NonNullable<Job['paymentSchedule']>;
  onAddToCalendar?: (stageKey: string, stageName: string) => void;
}

/**
 * Payment schedule breakdown — customer-facing display of staged payment plan.
 * Shown below totals on JobDetail when the job has a paymentSchedule with mode === 'staged'.
 * Accepts schedule data as a prop (does not reach into page-level state).
 *
 * Renders:
 * - Mandatory legal copy: "This provider collects payment directly. HirePanther does not process these payments."
 * - One row per stage: label, amount (CAD), trigger reason (plain language), estimated date, status
 * - Per-stage "Add to calendar" link (when estimatedDate is set) that hits /api/v1/job/:id/schedule/:key.ics
 */
const PaymentScheduleBreakdown = ({
  schedule,
  onAddToCalendar,
}: PaymentScheduleBreakdownProps) => {
  const { t } = useTranslation();

  // Plain-language labels for payment triggers
  const triggerLabel = (
    trigger:
      | 'on_acceptance'
      | 'provider_marks'
      | 'customer_approves'
      | 'on_completion'
  ): string => {
    switch (trigger) {
      case 'on_acceptance':
        return 'Due on acceptance';
      case 'provider_marks':
        return 'Provider confirms this stage';
      case 'customer_approves':
        return 'You confirm this stage';
      case 'on_completion':
        return 'Due on completion';
      default:
        return trigger;
    }
  };

  // Plain-language labels for payment stage status
  const statusLabel = (status: 'pending' | 'reached'): string => {
    switch (status) {
      case 'pending':
        return 'Upcoming';
      case 'reached':
        return 'Paid';
      default:
        return status;
    }
  };

  const formatDate = (value?: string | null): string => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleAddToCalendar = (stageKey: string, stageName: string) => {
    return () => {
      if (onAddToCalendar) {
        onAddToCalendar(stageKey, stageName);
      }
    };
  };

  return (
    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/60 space-y-3">
      {/* Header */}
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
        <BiWallet size={14} className="text-primary" />
        {t('jobs.payment_schedule', 'Payment Schedule')}
      </p>

      {/* Mandatory legal copy */}
      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-sky-50/60 border border-sky-200/70">
        <MdOutlineInfo size={14} className="text-sky-600 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          This provider collects payment directly. HirePanther does not process
          these payments.
        </p>
      </div>

      {/* Payment stages */}
      <div className="space-y-2">
        {schedule.stages.map((stage, idx) => {
          const isPaid = stage.status === 'reached';
          return (
            <div
              key={stage.key || idx}
              className="p-3 rounded-xl bg-white border border-slate-200/60 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <div
                    className={`p-1.5 rounded-lg shrink-0 mt-0.5 ${
                      isPaid
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : 'bg-primary/10 text-primary'
                    }`}
                  >
                    {isPaid ? (
                      <MdCheckCircle size={16} />
                    ) : (
                      <MdSchedule size={16} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">
                      {stage.label}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {triggerLabel(stage.trigger)}
                    </p>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs font-bold text-slate-800">
                    {formatCurrency(stage.amountMinor / 100)}
                  </p>
                  <span
                    className={`inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                      isPaid
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/70'
                        : 'bg-slate-100 text-slate-500 border border-slate-200/70'
                    }`}
                  >
                    {statusLabel(stage.status)}
                  </span>
                </div>
              </div>

              {/* Estimated date and calendar link */}
              {stage.estimatedDate && (
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <p className="text-[11px] text-slate-600 font-medium">
                    {formatDate(stage.estimatedDate)}
                  </p>
                  <button
                    type="button"
                    onClick={handleAddToCalendar(stage.key, stage.label)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:text-primary/80 transition-colors"
                    title="Add this stage to your calendar"
                  >
                    <FiCalendar size={12} />
                    Add to calendar
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentScheduleBreakdown;
